import { selectCurrentTeam } from 'containers/app/appSlice';
import { useLazyGetReservedFundsQuery } from '../api';
import { ReservedFund } from '../payouts.model';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppSelector } from '../../../hooks/typedHooks';
import { isPermissionDenied } from 'utils/apiUtils';
import DateUtils from 'utils/dateUtils';
import { HeldInReserveStatus } from '../constants';

interface GroupedReservedFunds {
  [key: string]: ReservedFund[];
}

export interface ReservedFundsFilter {
  status?: string;
  createdStart?: string;
  createdEnd?: string;
  availableStart?: string;
  availableEnd?: string;
  amount?: string;
  transactionId?: string;
}

const initialFilter: ReservedFundsFilter = {
  amount: null,
  transactionId: null,
  status: HeldInReserveStatus.HELD,
  createdEnd: null,
  createdStart: null,
  availableEnd: null,
  availableStart: null
};

export const usePaginatedReservedFundsQuery = () => {
  const currentTeam = useAppSelector(selectCurrentTeam);
  const [queryParams, setQueryParams] = useState<ReservedFundsFilter | undefined>(initialFilter);
  const [list, setList] = useState<ReservedFund[]>([]);
  const [groupedList, setGroupedList] = useState<GroupedReservedFunds>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [totalAmount, setTotalAmount] = useState<string>('0');
  const [totalCount, setTotalCount] = useState<number>(0);
  const [hasPermissionsToView, setHasPermissionsToView] = useState(true);
  const page = useRef(0);

  const [getReservedFundsData, reservedFundsState] = useLazyGetReservedFundsQuery();

  const {
    isSuccess: isReservedFundsSuccess,
    data: reservedFundsData,
    isFetching: isReservedFundsFetching,
    isError: isReservedFundsError,
    error: reservedFundsError
  } = reservedFundsState ?? {};

  const isInitListCall: boolean = page.current === 0;

  const groupByDate = (data: ReservedFund[]) => {
    const dateUpdatedList = data.map(item => ({
      ...item,
      createdAt: DateUtils.getDateInFormat({
        date: new Date(item.createdAt),
        dateFormat: 'EEEE, MMMM dd, yyyy'
      })
    }));

    const datesArray = dateUpdatedList.map(item => item.createdAt);
    const datesSet = new Set(datesArray);
    let dateGroupedList: GroupedReservedFunds = {};

    datesSet.forEach(date => {
      const dateList = dateUpdatedList.filter(item => item.createdAt === date);
      dateGroupedList = { ...dateGroupedList, [date]: dateList };
    });

    return dateGroupedList;
  };

  useEffect(() => {
    if (!isReservedFundsFetching && isReservedFundsSuccess) {
      setHasPermissionsToView(true);
      const reservedFunds = reservedFundsData?.data ?? [];
      const updatedList = isInitListCall ? reservedFunds : [...list, ...reservedFunds];
      const reservedFundsLeftToFetch = reservedFundsData.totalCount
        ? +reservedFundsData.totalCount - updatedList.length
        : 0;

      if (reservedFundsLeftToFetch > 0 && !reservedFunds?.length) {
        setHasMore(false);
      } else {
        setHasMore(reservedFundsLeftToFetch > 0);
      }

      setList(updatedList);
      setGroupedList(groupByDate(updatedList));

      if (page.current === 0) {
        setTotalAmount(reservedFundsData.totalAmount);
        setTotalCount(reservedFundsData.totalCount);
      }
      page.current = page.current + 1;
      setIsLoading(false);
    }
  }, [isReservedFundsFetching, isReservedFundsSuccess]);

  useEffect(() => {
    if (!isReservedFundsFetching && isReservedFundsError) {
      setIsLoading(false);
      setHasMore(false);
      if (isPermissionDenied(reservedFundsError?.['data']?.code, reservedFundsError?.['data']?.message)) {
        setHasPermissionsToView(false);
      }
      setIsError(true);
    }
  }, [isReservedFundsError, isReservedFundsFetching]);

  const resetListAndLoadMore = () => {
    page.current = 0;
    setList([]);
    setGroupedList({});
    loadMore();
  };

  useEffect(() => {
    resetListAndLoadMore();
  }, [queryParams]);

  const loadMore = useCallback(() => {
    setIsLoading(true);
    if (currentTeam?.id) {
      try {
        getReservedFundsData({
          teamId: currentTeam.id,
          page: page.current,
          limit: 10,
          ...queryParams
        });
        setIsLoading(false);
      } catch (err) {
        setIsError(true);
      }
    }
  }, [currentTeam?.id, page.current, queryParams]);

  const resetQuery = () => {
    resetListAndLoadMore();
  };

  return {
    isLoading,
    isFetching: isReservedFundsFetching,
    list,
    groupedList,
    totalAmount,
    totalCount,
    isError,
    loadMore,
    resetQuery,
    hasMore,
    isLoadingSuccess: isReservedFundsSuccess,
    hasPermissionsToView,
    setQueryParams,
    queryParams
  };
};
